export type ExportRows = Record<string, unknown>[];

export type OrganisationExportPack = {
  format: "raqoz.organisation-export";
  version: "3.3.0";
  generated_at: string;
  organisation: Record<string, unknown>;
  modules: Record<string, ExportRows>;
  record_counts: Record<string, number>;
  boundaries: {
    bank_execution: false;
    customer_references: false;
    attachments: string;
  };
};

const encoder = new TextEncoder();

export function flattenExportRow(
  row: Record<string, unknown>,
): Record<string, string | number | boolean | null> {
  return Object.fromEntries(
    Object.entries(row).map(([key, value]) => {
      if (value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean")
        return [key, value];
      return [key, JSON.stringify(value)];
    }),
  );
}

export function csvCell(value: unknown) {
  const text = value == null ? "" : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

export function rowsToCsv(rows: ExportRows) {
  const flattened = rows.map(flattenExportRow);
  const columns = Array.from(
    new Set(flattened.flatMap((row) => Object.keys(row))),
  ).sort();
  if (!columns.length) return "record_status\n\"No records\"";
  return [
    columns.map(csvCell).join(","),
    ...flattened.map((row) => columns.map((column) => csvCell(row[column])).join(",")),
  ].join("\n");
}

function xml(value: unknown) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function worksheetName(value: string, used: Set<string>) {
  const base = ["\\", "/", "?", "*", "[", "]", ":"]
    .reduce((name, character) => name.replaceAll(character, " "), value)
    .slice(0, 31) || "Sheet";
  let candidate = base;
  let suffix = 1;
  while (used.has(candidate)) {
    const tail = ` ${suffix++}`;
    candidate = `${base.slice(0, 31 - tail.length)}${tail}`;
  }
  used.add(candidate);
  return candidate;
}

export function exportPackToExcelXml(pack: OrganisationExportPack) {
  const used = new Set<string>();
  const sheets = Object.entries(pack.modules).map(([name, rows]) => {
    const flattened = rows.map(flattenExportRow);
    const columns = Array.from(
      new Set(flattened.flatMap((row) => Object.keys(row))),
    ).sort();
    const headings = columns.length ? columns : ["record_status"];
    const body = flattened.length ? flattened : [{ record_status: "No records" }];
    const rowXml = [headings, ...body.map((row) => headings.map((key) => row[key]))]
      .map(
        (row) =>
          `<Row>${row
            .map((value) => `<Cell><Data ss:Type="String">${xml(value)}</Data></Cell>`)
            .join("")}</Row>`,
      )
      .join("");
    return `<Worksheet ss:Name="${xml(worksheetName(name, used))}"><Table>${rowXml}</Table></Worksheet>`;
  });
  return `<?xml version="1.0"?><Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">${sheets.join("")}</Workbook>`;
}

let crcTable: Uint32Array | undefined;
function crc32(bytes: Uint8Array) {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let index = 0; index < 256; index++) {
      let value = index;
      for (let bit = 0; bit < 8; bit++)
        value = value & 1 ? 0xedb88320 ^ (value >>> 1) : value >>> 1;
      crcTable[index] = value >>> 0;
    }
  }
  let value = 0xffffffff;
  for (const byte of bytes) value = crcTable[(value ^ byte) & 0xff] ^ (value >>> 8);
  return (value ^ 0xffffffff) >>> 0;
}

function u16(value: number) {
  return [value & 0xff, (value >>> 8) & 0xff];
}

function u32(value: number) {
  return [value & 0xff, (value >>> 8) & 0xff, (value >>> 16) & 0xff, (value >>> 24) & 0xff];
}

export function buildCsvZip(modules: Record<string, ExportRows>) {
  const local: number[] = [];
  const central: number[] = [];
  let offset = 0;
  for (const [name, rows] of Object.entries(modules)) {
    const fileName = `${name.replace(/[^a-z0-9_-]+/gi, "-").toLowerCase()}.csv`;
    const file = encoder.encode(`\ufeff${rowsToCsv(rows)}`);
    const path = encoder.encode(fileName);
    const checksum = crc32(file);
    const header = [
      ...u32(0x04034b50), ...u16(20), ...u16(0x0800), ...u16(0),
      ...u16(0), ...u16(0), ...u32(checksum), ...u32(file.length),
      ...u32(file.length), ...u16(path.length), ...u16(0), ...path,
    ];
    local.push(...header, ...file);
    central.push(
      ...u32(0x02014b50), ...u16(20), ...u16(20), ...u16(0x0800), ...u16(0),
      ...u16(0), ...u16(0), ...u32(checksum), ...u32(file.length),
      ...u32(file.length), ...u16(path.length), ...u16(0), ...u16(0), ...u16(0),
      ...u16(0), ...u32(0), ...u32(offset), ...path,
    );
    offset += header.length + file.length;
  }
  const count = Object.keys(modules).length;
  const end = [
    ...u32(0x06054b50), ...u16(0), ...u16(0), ...u16(count), ...u16(count),
    ...u32(central.length), ...u32(local.length), ...u16(0),
  ];
  return new Uint8Array([...local, ...central, ...end]);
}

export async function sha256Hex(value: string | Uint8Array) {
  const bytes = typeof value === "string" ? encoder.encode(value) : value;
  const digest = await crypto.subtle.digest("SHA-256", new Uint8Array(bytes).buffer);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
